import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  FlaskConical,
  ImagePlus,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Card } from "@/platform/ui/card";
import { GradingProgress } from "@/platform/ui/grading-progress";
import { showCorrectionError } from "@/platform/billing/errors";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import {
  gradeDiscursive2aFase,
  listUspDiscursives,
  listUspDiscursiveDisciplines,
  fetchQuestionImagePath,
  uploadUspDiscursiveImages,
  type GradeDiscursive2aResult,
} from "@/tenants/usp/features/discursive/api";

// Fallback caso a query de disciplinas falhe; a lista real vem do corpus.
const DISCIPLINES_FALLBACK = [
  "matematica",
  "fisica",
  "quimica",
  "biologia",
  "historia",
  "geografia",
  "portugues",
  "literatura",
] as const;

const ERROR_LABELS: Record<string, string> = {
  conteudo: "Erro de conteúdo",
  calculo: "Erro de cálculo",
  interpretacao: "Erro de interpretação",
  comunicacao: "Erro de comunicação",
  completude: "Resposta incompleta",
  sem_erro: "Sem erro relevante",
};

export function UspSegundaFasePage() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [discipline, setDiscipline] = useState<string>("matematica");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<"text" | "image">("text");
  const [answer, setAnswer] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GradeDiscursive2aResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const query = useQuery({
    queryKey: ["usp", "discursives", discipline],
    queryFn: () => listUspDiscursives(discipline),
  });
  const disciplinesQuery = useQuery({
    queryKey: ["usp", "discursive-disciplines"],
    queryFn: listUspDiscursiveDisciplines,
  });

  const disciplines =
    disciplinesQuery.data && disciplinesQuery.data.length > 0
      ? disciplinesQuery.data
      : [...DISCIPLINES_FALLBACK];
  const questions = query.data ?? [];
  const selected = useMemo(
    () => questions.find((q) => q.id === selectedId) ?? questions[0] ?? null,
    [questions, selectedId],
  );

  async function handleSubmit() {
    if (!session || !selected) {
      toast.error("Selecione uma questão.");
      return;
    }
    if (mode === "text" && answer.trim().length < 20) {
      toast.error("Escreva sua resposta (mín. 20 caracteres).");
      return;
    }
    if (mode === "image" && files.length === 0) {
      toast.error("Anexe ao menos uma foto.");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      let imagePaths: string[] | undefined;
      if (mode === "image") {
        imagePaths = await uploadUspDiscursiveImages({
          userId: session.user.id,
          uuid: crypto.randomUUID(),
          files,
        });
      }
      // Questão com gráfico/tabela/figura: envia a página do enunciado p/ o
      // corretor não avaliar visual às cegas.
      const questionImagePath =
        selected.has_graph || selected.has_image || selected.has_table
          ? ((await fetchQuestionImagePath(selected.exam_paper_id, selected.page_number)) ??
            undefined)
          : undefined;

      const res = await gradeDiscursive2aFase({
        discipline: selected.discipline,
        question_id: selected.id,
        stem: selected.stem,
        items: selected.alternatives?.items,
        expected_approach: selected.expected_answer ?? undefined,
        student_answer: mode === "text" ? answer : undefined,
        image_paths: imagePaths,
        question_image_path: questionImagePath,
      });
      setResult(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      showCorrectionError(toast, err, navigate);
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return <DiscursiveResult result={result} onReset={() => setResult(null)} />;
  }

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <GradingProgress active={submitting} />
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-20 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-12 top-32 w-12 opacity-50 animate-float" />

      <Container size="default" className="relative py-12 md:py-16">
        <Link
          to="/usp/painel"
          className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
        >
          <ArrowLeft className="size-3.5" />
          Painel
        </Link>

        <header className="mt-8 max-w-2xl">
          <Badge variant="accentSolid" className="gap-2">
            <FlaskConical className="size-3" />
            Discursivas 2ª fase
          </Badge>
          <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Treine as discursivas da 2ª fase Fuvest.
          </h1>
          <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] leading-relaxed">
            Correção item a item com comparação à abordagem esperada Fuvest. Identifica lacunas
            conceituais e classifica o tipo de erro predominante.
          </p>
        </header>

        <Card className="mt-8 p-6 md:p-8">
          {/* Disciplina */}
          <div>
            <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Disciplina
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {disciplines.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    setDiscipline(d);
                    setSelectedId(null);
                  }}
                  className={cn(
                    "rounded-full px-3.5 py-1.5 text-[length:var(--text-body-sm)] font-medium capitalize transition-colors",
                    discipline === d ? "text-white" : "border border-[--color-border] text-[color:var(--color-muted-foreground)]",
                  )}
                  style={discipline === d ? { backgroundColor: "var(--color-accent)" } : undefined}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Questão */}
          <div className="mt-6">
            <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              Questão
            </label>
            {query.isLoading ? (
              <Loader2 className="mt-2 size-4 animate-spin" />
            ) : questions.length === 0 ? (
              <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Nenhuma discursiva de {discipline} no corpus ainda.
              </p>
            ) : (
              <select
                value={selected?.id ?? ""}
                onChange={(e) => setSelectedId(e.target.value)}
                className="mt-2 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3.5 py-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
              >
                {questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    Fuvest {q.year} · Q{q.number} — {q.stem.slice(0, 60)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {selected && (
            <div className="mt-5 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/30 p-4">
              <p className="whitespace-pre-line text-[length:var(--text-body-sm)] leading-relaxed">{selected.stem}</p>
              {selected.alternatives?.items && (
                <ul className="mt-3 space-y-1.5">
                  {selected.alternatives.items.map((it) => (
                    <li key={it.id} className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                      <strong>({it.id})</strong> {it.sub_question}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Mode */}
          <div className="mt-6 inline-flex rounded-full border border-[--color-border] bg-[color:var(--color-background)] p-1">
            <button
              type="button"
              onClick={() => setMode("text")}
              className={cn("inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium transition-colors", mode === "text" ? "bg-[color:var(--color-accent)] text-white" : "text-[color:var(--color-muted-foreground)]")}
            >
              <FileText className="size-3.5" /> Digitar
            </button>
            <button
              type="button"
              onClick={() => setMode("image")}
              className={cn("inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium transition-colors", mode === "image" ? "bg-[color:var(--color-accent)] text-white" : "text-[color:var(--color-muted-foreground)]")}
            >
              <ImagePlus className="size-3.5" /> Foto
            </button>
          </div>

          {mode === "text" ? (
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={10}
              placeholder="Escreva sua resolução completa, com justificativas e cálculos..."
              className="mt-4 w-full rounded-xl border border-[--color-border] bg-[color:var(--color-background)] p-4 text-[length:var(--text-body-sm)] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
            />
          ) : (
            <div className="mt-4">
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 6))} className="hidden" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[--color-border] p-8 text-[color:var(--color-muted-foreground)] transition-colors hover:border-[--color-accent]/40"
              >
                <ImagePlus className="size-8" />
                <span className="text-[length:var(--text-body-sm)]">
                  {files.length > 0 ? `${files.length} foto(s)` : "Anexar foto da resolução manuscrita"}
                </span>
              </button>
            </div>
          )}

          <div className="mt-6 sm:flex sm:justify-end">
            <Button
              size="lg"
              variant="gradient"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!selected}
              className="w-full sm:w-auto"
            >
              {submitting ? "Corrigindo…" : "Corrigir resposta"}
              {!submitting && <Sparkles className="size-4" />}
            </Button>
          </div>
        </Card>
      </Container>
    </div>
  );
}

function DiscursiveResult({ result, onReset }: { result: GradeDiscursive2aResult; onReset: () => void }) {
  const pct = result.max_score > 0 ? (result.total_score / result.max_score) * 100 : 0;
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-25 blur-2xl animate-blob" />
      <Container size="default" className="relative py-12 md:py-16">
        <Badge className="gap-1.5" style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}>
          <CheckCircle2 className="size-3" />
          Correção concluída
        </Badge>
        <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight">
          Nota: <span className="text-gradient-accent">{result.total_score}/{result.max_score}</span>
        </h1>
        <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
          Erro predominante: <strong>{ERROR_LABELS[result.predominant_error_type] ?? result.predominant_error_type}</strong>
        </p>

        <div className="mt-3 h-2 max-w-md overflow-hidden rounded-full bg-[color:var(--color-muted)]">
          <div className="h-full" style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? "var(--color-success)" : pct >= 40 ? "var(--color-warning)" : "var(--color-danger)" }} />
        </div>

        {/* Itens */}
        <section className="mt-8 space-y-3">
          {result.items_scores.map((it) => (
            <div key={it.id} className="rounded-xl border border-[--color-border] bg-[color:var(--color-card)] p-5">
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold">Item ({it.id})</span>
                <span data-tabular="true" className="font-semibold" style={{ color: "var(--color-accent-deep)" }}>
                  {it.score}/{it.max_score}
                </span>
              </div>
              <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                {it.feedback}
              </p>
              {it.lacuna_identificada && (
                <p className="mt-2 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
                  Lacuna: {it.lacuna_identificada}
                </p>
              )}
            </div>
          ))}
        </section>

        <section className="mt-8">
          <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold">Feedback geral</h2>
          <p className="mt-3 whitespace-pre-line text-[length:var(--text-body)] leading-relaxed text-[color:var(--color-muted-foreground)]">
            {result.feedback}
          </p>
        </section>

        {result.next_steps.length > 0 && (
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6">
            <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold">Próximos passos</h2>
            <ul className="mt-3 space-y-2">
              {result.next_steps.map((s, i) => (
                <li key={i} className="flex gap-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                  <span style={{ color: "var(--color-accent-deep)" }}>→</span> {s}
                </li>
              ))}
            </ul>
          </section>
        )}

        {result.graded_without_reference && (
          <p className="mt-6 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/40 p-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            A Fuvest não publicou a abordagem esperada oficial deste ano. A
            correção e o comentário abaixo refletem a expectativa técnica do
            corretor, não o gabarito oficial da banca.
          </p>
        )}

        {result.expected_answer_comments && (
          <details className="mt-6 rounded-xl border border-[--color-border] bg-[color:var(--color-muted)]/30 p-4">
            <summary className="cursor-pointer text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
              {result.graded_without_reference
                ? "Expectativa técnica do corretor"
                : "Abordagem esperada Fuvest"}
            </summary>
            <p className="mt-3 whitespace-pre-line text-[length:var(--text-body-sm)] leading-relaxed">
              {result.expected_answer_comments}
            </p>
          </details>
        )}

        <div className="mt-8 flex gap-3">
          <Button variant="primary" onClick={onReset}>
            <FlaskConical className="size-4" />
            Corrigir outra
          </Button>
          <Button asChild variant="outline">
            <Link to="/usp/painel">Voltar ao painel</Link>
          </Button>
        </div>
      </Container>
    </div>
  );
}
